import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Plot from 'react-plotly.js';
import WorkflowSidebar from './components/WorkflowSidebar';
import MethodCard from './components/MethodCard';
import CancelButton from './components/CancelButton';
import apiClient from '../../api/client';
import SaveResultsWizard from '../../components/chemistry/SaveResultsWizard';
import { useWorkbenchContext } from '../../hooks/useWorkbenchContext';
import WorkbenchContextBar from '../../components/workbench/WorkbenchContextBar';
import ToolSwitcher from '../../components/workbench/ToolSwitcher';
import { useToolStep } from '../../hooks/useWorkbenchContext';

const STEPS = [
  { name: 'Upload' },
  { name: 'Map' },
  { name: 'Fit' },
  { name: 'Review' },
  { name: 'Export' },
];

const SAMPLE_DATA = `0.001,2.1
0.003,3.8
0.01,8.2
0.03,15.4
0.1,35.7
0.3,62.3
1,85.6
3,94.8
10,97.5
30,99.1`;

const WorkbenchIC50: React.FC = () => {
  const { smiles: ctxSmiles, moleculeId: ctxMoleculeId, fileId: ctxFileId, projectId: ctxProjectId } = useWorkbenchContext();
  const [step, setStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [rawText, setRawText] = useState('');
  const [parsedPoints, setParsedPoints] = useState<{ i: number; response: number }[]>([]);
  const [parseError, setParseError] = useState('');
  const [units, setUnits] = useState('\u00b5M');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const { bailOutText, setHasSaved } = useToolStep();

  const parseData = useCallback((text: string) => {
    setParseError('');
    const lines = text.trim().split('\n').filter((l) => l.trim());
    const points: { i: number; response: number }[] = [];
    for (let idx = 0; idx < lines.length; idx++) {
      const line = lines[idx];
      const parts = line.split(/[,\t;]+/).map((s) => s.trim());
      if (parts.length < 2) {
        // Skip header-like lines
        if (idx === 0 && isNaN(Number(parts[0]))) continue;
        setParseError(`Line ${idx + 1}: expected 2 columns, got ${parts.length}`);
        return;
      }
      const conc = parseFloat(parts[0]);
      const resp = parseFloat(parts[1]);
      if (isNaN(conc) || isNaN(resp)) {
        if (idx === 0) continue; // skip header
        setParseError(`Line ${idx + 1}: non-numeric values`);
        return;
      }
      points.push({ i: conc, response: resp });
    }
    if (points.length < 4) {
      setParseError('Need at least 4 data points for a reliable fit');
      return;
    }
    setParsedPoints(points);
    setCompletedSteps((prev) => Array.from(new Set([...prev, 0])));
    setStep(1);
  }, []);

  const confirmMapping = () => {
    setCompletedSteps((prev) => Array.from(new Set([...prev, 1])));
    setStep(2);
  };

  const runFit = async () => {
    setIsRunning(true);
    setError('');
    setResult(null);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await apiClient.post('/v2/kinetics/fit/ic50', {
        points: parsedPoints,
      }, { signal: controller.signal });
      setResult(resp.data);
      setCompletedSteps((prev) => Array.from(new Set([...prev, 2])));
      setStep(3);
    } catch (err: any) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        setError('Fit cancelled by user');
      } else {
        setError('Fitting service unavailable. Please try again or contact support.');
      }
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
  };

  const copyResults = () => {
    if (!result) return;
    const lines: string[] = [];
    if (result.params) {
      Object.entries(result.params as Record<string, number>).forEach(([k, v]) =>
        lines.push(`${k}: ${(v as number).toPrecision(6)}`)
      );
    }
    if (result.r2 != null) lines.push(`R2: ${(result.r2 as number).toFixed(6)}`);
    navigator.clipboard.writeText(lines.join('\n'));
  };

  const downloadCSV = () => {
    if (!result) return;
    const lines = ['parameter,value'];
    if (result.params) {
      Object.entries(result.params as Record<string, number>).forEach(([k, v]) => {
        lines.push(`${k},${v}`);
      });
    }
    if (result.r2 != null) lines.push(`R2,${result.r2}`);
    if (result.data) {
      lines.push('', 'concentration,observed,residual');
      const resMap = new Map<number, number>();
      if (result.residuals) {
        (result.residuals.x as number[]).forEach((x: number, i: number) => resMap.set(x, (result.residuals.y as number[])[i]));
      }
      (result.data.x as number[]).forEach((x: number, i: number) => {
        lines.push(`${x},${(result.data.y as number[])[i]},${resMap.get(x) ?? ''}`);
      });
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ic50_results.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadSample = () => {
    setRawText(SAMPLE_DATA);
  };

  return (
    <>
      <WorkbenchContextBar bailOutText={bailOutText} />
      <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
      <WorkflowSidebar steps={STEPS} currentStep={step} completedSteps={completedSteps} onStepClick={setStep} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Status bar */}
        <div
          style={{
            padding: '8px 20px',
            background: 'var(--hover-bg)',
            borderBottom: '1px solid var(--card-border)',
            fontFamily: 'monospace',
            fontSize: 12,
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <ToolSwitcher currentToolId="ic50" />
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Engine: SciPy</span>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Step {step + 1}/{STEPS.length}</span>
          <div style={{ flex: 1 }} />
          <CancelButton onCancel={handleCancel} isRunning={isRunning} label="Stop Fit" />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Step 0: Upload */}
            {step === 0 && (
              <div>
                <h2 style={{ color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 }}>
                  Paste or upload dose-response data
                </h2>
                <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
                  CSV format: concentration, response (one per line). Headers are auto-detected.
                </p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button onClick={loadSample} style={btnStyle}>Load Sample Data</button>
                  <label style={{ ...btnStyle, cursor: 'pointer' }}>
                    Upload File
                    <input
                      type="file"
                      accept=".csv,.txt,.tsv"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) f.text().then(setRawText);
                      }}
                    />
                  </label>
                </div>
                <textarea
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  placeholder="concentration,response&#10;0.001,2.1&#10;0.01,8.2&#10;0.1,35.7&#10;1,85.6&#10;10,97.5"
                  style={textareaStyle}
                  rows={12}
                />
                {parseError && <div style={errorStyle}>{parseError}</div>}
                <button
                  onClick={() => parseData(rawText)}
                  disabled={!rawText.trim()}
                  style={{ ...primaryBtnStyle, marginTop: 12, opacity: rawText.trim() ? 1 : 0.5 }}
                >
                  Parse Data
                </button>
              </div>
            )}

            {/* Step 1: Map */}
            {step === 1 && (
              <div>
                <h2 style={{ color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 }}>
                  Data Preview ({parsedPoints.length} points)
                </h2>
                <div style={{ background: 'var(--card-bg)', borderRadius: 10, border: '1px solid var(--card-border)', overflow: 'hidden', marginBottom: 16 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                        <th style={thStyle}>#</th>
                        <th style={thStyle}>Concentration</th>
                        <th style={thStyle}>Response</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedPoints.map((p, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid var(--card-bg)' }}>
                          <td style={tdStyle}>{idx + 1}</td>
                          <td style={tdStyle}>{p.i}</td>
                          <td style={tdStyle}>{p.response}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <details style={{ marginBottom: 16 }}>
                  <summary style={{ color: '#a7abb6', cursor: 'pointer', fontSize: 13 }}>Expert settings</summary>
                  <div style={{ padding: '12px 0', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <label style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      Units:{' '}
                      <select value={units} onChange={(e) => setUnits(e.target.value)} style={selectStyle}>
                        <option value="\u00b5M">{'\u00b5'}M</option>
                        <option value="nM">nM</option>
                        <option value="pM">pM</option>
                      </select>
                    </label>
                  </div>
                </details>
                <button onClick={confirmMapping} style={primaryBtnStyle}>Confirm &amp; Continue</button>
              </div>
            )}

            {/* Step 2: Fit */}
            {step === 2 && (
              <div>
                <h2 style={{ color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 }}>
                  Run IC50 Curve Fit
                </h2>
                <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
                  Fitting {parsedPoints.length} data points using 4-Parameter Logistic (4PL) regression.
                </p>
                {!isRunning && !result && (
                  <button onClick={runFit} style={primaryBtnStyle}>Fit Curves</button>
                )}
                {isRunning && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={spinnerStyle} />
                    <span style={{ color: '#f59e0b', fontSize: 14 }}>Fitting curves...</span>
                  </div>
                )}
                {error && <div style={errorStyle}>{error}</div>}
              </div>
            )}

            {/* Step 3: Review */}
            {step === 3 && result && (
              <div>
                <h2 style={{ color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 16, marginTop: 0 }}>
                  Fit Results
                </h2>

                {/* Parameter cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
                  {result.params && Object.entries(result.params as Record<string, number>).map(([key, value]) => (
                    <div key={key} style={resultCardStyle}>
                      <div style={resultLabelStyle}>{key}</div>
                      <div style={resultValueStyle}>
                        {key.toLowerCase() === 'ic50' ? (value as number).toPrecision(4) : (value as number).toFixed(2)}
                      </div>
                    </div>
                  ))}
                  {result.r2 != null && (
                    <div style={resultCardStyle}>
                      <div style={resultLabelStyle}>R²</div>
                      <div style={resultValueStyle}>{(result.r2 as number).toFixed(6)}</div>
                    </div>
                  )}
                </div>

                {/* Dose-response curve: observed + fitted */}
                {result.data && result.fit && (
                  <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                    <Plot
                      data={[
                        {
                          x: result.data.x,
                          y: result.data.y,
                          type: 'scatter' as const,
                          mode: 'markers' as const,
                          name: 'Observed',
                          marker: { color: '#f59e0b', size: 8 },
                        },
                        {
                          x: result.fit.x,
                          y: result.fit.y,
                          type: 'scatter' as const,
                          mode: 'lines' as const,
                          name: 'Fitted (4PL)',
                          line: { color: '#3b82f6', width: 2 },
                        },
                      ]}
                      layout={{
                        title: { text: 'Dose-Response Curve', font: { color: '#f5f5f5', size: 14 } },
                        xaxis: {
                          title: { text: `Concentration (${units})`, font: { color: '#a7abb6' } },
                          type: 'log',
                          gridcolor: 'rgba(255,255,255,0.06)',
                          tickfont: { color: '#a7abb6' },
                        },
                        yaxis: {
                          title: { text: 'Response (%)', font: { color: '#a7abb6' } },
                          gridcolor: 'rgba(255,255,255,0.06)',
                          tickfont: { color: '#a7abb6' },
                        },
                        paper_bgcolor: 'transparent',
                        plot_bgcolor: 'transparent',
                        legend: { font: { color: '#a7abb6' }, x: 0.7, y: 0.95 },
                        margin: { t: 40, r: 20, b: 50, l: 60 },
                        height: 360,
                      }}
                      config={{ displayModeBar: false, responsive: true }}
                      style={{ width: '100%' }}
                    />
                  </div>
                )}

                {/* Residuals chart */}
                {result.residuals && (
                  <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                    <Plot
                      data={[
                        {
                          x: result.residuals.x,
                          y: result.residuals.y,
                          type: 'bar' as const,
                          name: 'Residuals',
                          marker: {
                            color: (result.residuals.y as number[]).map((v: number) => v >= 0 ? '#22c55e' : '#ef4444'),
                          },
                        },
                      ]}
                      layout={{
                        title: { text: 'Residuals', font: { color: '#f5f5f5', size: 14 } },
                        xaxis: {
                          title: { text: `Concentration (${units})`, font: { color: '#a7abb6' } },
                          type: 'log',
                          gridcolor: 'rgba(255,255,255,0.06)',
                          tickfont: { color: '#a7abb6' },
                        },
                        yaxis: {
                          title: { text: 'Residual', font: { color: '#a7abb6' } },
                          gridcolor: 'rgba(255,255,255,0.06)',
                          tickfont: { color: '#a7abb6' },
                          zeroline: true,
                          zerolinecolor: 'rgba(255,255,255,0.2)',
                        },
                        paper_bgcolor: 'transparent',
                        plot_bgcolor: 'transparent',
                        margin: { t: 40, r: 20, b: 50, l: 60 },
                        height: 240,
                        showlegend: false,
                      }}
                      config={{ displayModeBar: false, responsive: true }}
                      style={{ width: '100%' }}
                    />
                  </div>
                )}

                <button onClick={() => { setCompletedSteps((prev) => Array.from(new Set([...prev, 3]))); setStep(4); }} style={primaryBtnStyle}>
                  Continue to Export
                </button>
              </div>
            )}

            {/* Step 4: Export */}
            {step === 4 && (
              <div>
                <h2 style={{ color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 16, marginTop: 0 }}>
                  Export Results
                </h2>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={copyResults} style={btnStyle}>Copy Results</button>
                  <button onClick={downloadCSV} style={primaryBtnStyle}>Download CSV</button>
                  <button onClick={() => setSaveOpen(true)} style={btnStyle}>Save to Project</button>
                </div>
                <SaveResultsWizard isOpen={saveOpen} onClose={() => setSaveOpen(false)} rawData={result} toolName="IC50 Fitting" toolKey="ic50" smiles={ctxSmiles} moleculeId={ctxMoleculeId} fileId={ctxFileId} projectId={ctxProjectId} />
              </div>
            )}
          </div>

          {/* Method card panel */}
          <div style={{ width: 320, flexShrink: 0 }}>
            <MethodCard
              engine="SciPy"
              title="4-Parameter Logistic (4PL)"
              description="Non-linear regression for sigmoidal dose-response curves. Estimates inflection point (IC50), steepness (Hill slope), and asymptotes."
              equation="y = Bottom + (Top - Bottom) / (1 + (x / IC50)^HillSlope)"
              parameters={[
                { name: 'Top', desc: 'Upper asymptote (max response)' },
                { name: 'Bottom', desc: 'Lower asymptote (min response)' },
                { name: 'IC50', desc: 'Inflection point concentration' },
                { name: 'HillSlope', desc: 'Steepness of the curve' },
              ]}
              reference="Hill, A.V. (1910). J Physiol 40:iv-vii"
              source="scipy.optimize.curve_fit with Levenberg-Marquardt algorithm"
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

// Shared styles
const btnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)',
  borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500,
};

const primaryBtnStyle: React.CSSProperties = {
  ...btnStyle, background: '#f59e0b', color: 'var(--content-bg)', border: '1px solid #f59e0b', fontWeight: 600,
};

const textareaStyle: React.CSSProperties = {
  width: '100%', padding: 14, background: 'var(--card-bg)', border: '1px solid var(--card-border)',
  borderRadius: 10, color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace',
  resize: 'vertical', outline: 'none', boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  padding: '4px 8px', background: 'var(--card-bg)', border: '1px solid var(--card-border)',
  borderRadius: 6, color: 'var(--text-primary)', fontSize: 13,
};

const thStyle: React.CSSProperties = {
  padding: '8px 12px', textAlign: 'left', color: '#a7abb6', fontSize: 11,
  fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
};

const tdStyle: React.CSSProperties = {
  padding: '6px 12px', color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: 13,
};

const errorStyle: React.CSSProperties = {
  marginTop: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.1)',
  border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fb7185', fontSize: 13,
};

const spinnerStyle: React.CSSProperties = {
  width: 20, height: 20, border: '3px solid var(--card-border)', borderTopColor: '#f59e0b',
  borderRadius: '50%', animation: 'spin 0.8s linear infinite',
};

const resultCardStyle: React.CSSProperties = {
  background: 'var(--card-bg)', border: '1px solid var(--card-border)',
  borderRadius: 10, padding: '12px 14px',
};

const resultLabelStyle: React.CSSProperties = {
  color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
};

const resultValueStyle: React.CSSProperties = {
  color: '#f59e0b', fontSize: 18, fontWeight: 700, fontFamily: 'monospace',
};

export default WorkbenchIC50;
